import * as React from "react";
import {useEffect, useImperativeHandle, useRef, useState, forwardRef} from "react";
import {useTranslation} from "react-i18next";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import VideocamOffOutlinedIcon from "@mui/icons-material/VideocamOffOutlined";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import MicOffOutlinedIcon from "@mui/icons-material/MicOffOutlined";
import SecondaryButton from "../../../common/buttons/SecondaryButton";
import "./DeviceCheck.css";

type CheckState = "idle" | "checking" | "ready" | "denied" | "error";

export interface DeviceCheckHandle {
    /** Release the camera/mic so the session window can acquire them. */
    stop: () => void;
}

// Pre-session health check shown before the patient joins: a live self-preview,
// a microphone level meter and per-device status, so they confirm camera/mic
// work instead of entering the session blind. Non-blocking — the join button
// stays available regardless (video is optional in sessions).
const DeviceCheck = forwardRef<DeviceCheckHandle, {}>((_props, ref) => {
    const {t} = useTranslation();
    const videoRef = useRef<HTMLVideoElement>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const audioCtxRef = useRef<AudioContext | null>(null);
    const rafRef = useRef<number | null>(null);

    const [state, setState] = useState<CheckState>("idle");
    const [hasCamera, setHasCamera] = useState(false);
    const [hasMic, setHasMic] = useState(false);
    const [micLevel, setMicLevel] = useState(0); // 0..1

    const stop = () => {
        if (rafRef.current != null) {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
        }
        if (audioCtxRef.current) {
            audioCtxRef.current.close().catch(() => {});
            audioCtxRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        setMicLevel(0);
    };

    useImperativeHandle(ref, () => ({stop}));

    // Always release devices when this component goes away.
    useEffect(() => stop, []);

    // Attach the stream once the (always-mounted) <video> is showing. Doing this
    // in an effect avoids the race where srcObject was set before the element
    // existed.
    useEffect(() => {
        const el = videoRef.current;
        if (hasCamera && el && streamRef.current) {
            if (el.srcObject !== streamRef.current) {
                el.srcObject = streamRef.current;
            }
            el.play().catch(() => {});
        }
    }, [hasCamera]);

    const startMeter = (stream: MediaStream) => {
        if (stream.getAudioTracks().length === 0) return;
        try {
            const Ctx = window.AudioContext || (window as any).webkitAudioContext;
            const ctx: AudioContext = new Ctx();
            audioCtxRef.current = ctx;
            const source = ctx.createMediaStreamSource(stream);
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 512;
            source.connect(analyser);
            const data = new Uint8Array(analyser.frequencyBinCount);
            const tick = () => {
                analyser.getByteTimeDomainData(data);
                let sum = 0;
                for (let i = 0; i < data.length; i++) {
                    const v = (data[i] - 128) / 128;
                    sum += v * v;
                }
                const rms = Math.sqrt(sum / data.length);
                // Boost a little so normal speech fills most of the meter.
                setMicLevel(Math.min(1, rms * 2.2));
                rafRef.current = requestAnimationFrame(tick);
            };
            tick();
        } catch {
            /* metering is best-effort */
        }
    };

    const runCheck = async () => {
        stop();
        setState("checking");
        if (!navigator.mediaDevices?.getUserMedia) {
            setState("error");
            return;
        }
        let stream: MediaStream | null = null;
        try {
            stream = await navigator.mediaDevices.getUserMedia({video: true, audio: true});
        } catch (e: any) {
            const name = e?.name;
            if (name === "NotAllowedError" || name === "SecurityError") {
                setState("denied");
                return;
            }
            // No camera (or it is busy): still try audio-only so the mic can be
            // verified and the patient sees exactly what is missing.
            try {
                stream = await navigator.mediaDevices.getUserMedia({video: false, audio: true});
            } catch (e2: any) {
                setState(e2?.name === "NotAllowedError" ? "denied" : "error");
                return;
            }
        }
        streamRef.current = stream;
        const camOn = stream.getVideoTracks().length > 0;
        const micOn = stream.getAudioTracks().length > 0;
        setHasCamera(camOn);
        setHasMic(micOn);
        if (camOn && videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.play().catch(() => {});
        }
        startMeter(stream);
        setState("ready");
    };

    return (
        <div className="device-check">
            <div className="device-check-preview">
                <video
                    ref={videoRef}
                    className="device-check-video"
                    muted
                    playsInline
                    autoPlay
                    style={{display: hasCamera ? "block" : "none"}}
                />
                {!hasCamera && (
                    <div className="device-check-placeholder">
                        <VideocamOffOutlinedIcon />
                        <span>
                            {state === "ready"
                                ? t("No camera detected")
                                : t("Camera preview will appear here")}
                        </span>
                    </div>
                )}
            </div>

            {state === "ready" && (
                <div className="device-check-statuses">
                    <span className={"device-check-chip " + (hasCamera ? "ok" : "off")}>
                        {hasCamera ? <VideocamOutlinedIcon /> : <VideocamOffOutlinedIcon />}
                        {hasCamera ? t("Camera works") : t("No camera")}
                    </span>
                    <span className={"device-check-chip " + (hasMic ? "ok" : "off")}>
                        {hasMic ? <MicNoneOutlinedIcon /> : <MicOffOutlinedIcon />}
                        {hasMic ? t("Microphone works") : t("No microphone")}
                    </span>
                </div>
            )}

            {state === "ready" && hasMic && (
                <div className="device-check-meter" aria-label={t("Microphone level")}>
                    <div className="device-check-meter-fill" style={{width: `${Math.round(micLevel * 100)}%`}} />
                </div>
            )}

            {state === "denied" && (
                <p className="device-check-hint device-check-hint-warn">
                    {t("Camera and microphone are blocked. Allow access in your browser to run the check.")}
                </p>
            )}
            {state === "error" && (
                <p className="device-check-hint device-check-hint-warn">
                    {t("Could not access the camera or microphone on this device.")}
                </p>
            )}

            <SecondaryButton onClick={runCheck} disabled={state === "checking"}>
                {state === "idle"
                    ? t("Test camera and microphone")
                    : state === "checking"
                    ? t("Checking…")
                    : t("Test again")}
            </SecondaryButton>
        </div>
    );
});

export default DeviceCheck;
